"use client";

import Link from "next/link";
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ArrowRightIcon } from "./icons";
import { HomeSportIcon } from "./sport-icons";
import {
  HOME_MORE_SPORTS,
  HOME_PRIMARY_CHIPS,
  homeBoardLink,
  homeSportMessage,
  moreSport,
  type HomeSportToggle,
} from "./home-sport-toggles";

/**
 * Client island for the homepage sport chips. The server renders every card
 * sport's cards; this only tracks the chosen sport, hides the other panels,
 * shows the leaderboard card for menu sports without cards and points the
 * link under the cards at the matching board.
 */

type SportChoiceState = { sport: HomeSportToggle; skiOffSeason: boolean };

const SportChoice = createContext<SportChoiceState>({ sport: "all", skiOffSeason: false });
const SetSportChoice = createContext<(sport: HomeSportToggle) => void>(() => {});

export function SportChoiceProvider({ skiOffSeason, children }: { skiOffSeason: boolean; children: ReactNode }) {
  const [sport, setSport] = useState<HomeSportToggle>("all");
  return (
    <SetSportChoice.Provider value={setSport}>
      <SportChoice.Provider value={{ sport, skiOffSeason }}>{children}</SportChoice.Provider>
    </SetSportChoice.Provider>
  );
}

function chipClass(active: boolean): string {
  return `flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-full border px-4 text-[14px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark ${
    active ? "border-navy bg-navy text-white" : "border-navy/20 bg-white text-navy hover:bg-[#EEF2EC]"
  }`;
}

function CheckIcon({ visible }: { visible: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={`shrink-0 ${visible ? "opacity-100" : "opacity-0"}`}>
      <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#15727F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MoreSportsMenu() {
  const { sport } = useContext(SportChoice);
  const setSport = useContext(SetSportChoice);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const chosen = moreSport(sport);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (wrapRef.current && event.target instanceof Node && wrapRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative min-w-0"
      onBlur={(event) => {
        // Close when keyboard focus tabs out of the menu (blur bubbles in React).
        if (!wrapRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={chosen ? `More sports, showing ${chosen.label}` : "More sports"}
        onClick={() => setOpen((value) => !value)}
        data-ph-capture-attribute-home-cta="conditions-chip-more"
        className={`${chipClass(Boolean(chosen))} max-w-[168px] pl-[15px] pr-3 ${open && !chosen ? "border-navy" : ""}`}
      >
        <span className="min-w-0 truncate">{chosen ? chosen.chip : "More sports"}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className={`shrink-0 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
        >
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div
        id={menuId}
        hidden={!open}
        className="absolute right-0 top-[52px] z-20 w-[256px] rounded-[16px] border border-line bg-white p-1.5 shadow-[0_22px_48px_rgba(22,35,59,0.22)]"
      >
        <ul aria-label="More sports" className="m-0 list-none p-0">
          {HOME_MORE_SPORTS.map((entry) => {
            const active = entry.key === sport;
            return (
              <li key={entry.key}>
                <button
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setSport(entry.key);
                    setOpen(false);
                    buttonRef.current?.focus();
                  }}
                  data-ph-capture-attribute-home-cta={`conditions-chip-${entry.key}`}
                  className={`flex min-h-11 w-full items-center justify-between gap-2.5 rounded-[10px] px-2.5 text-left text-[14.5px] text-navy transition-colors hover:bg-warm-bg focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal-dark ${
                    active ? "bg-[#EEF6F7] font-bold" : "font-medium"
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="text-teal-dark">
                      <HomeSportIcon sport={entry.key} size={16} />
                    </span>
                    <span>{entry.label}</span>
                  </span>
                  <CheckIcon visible={active} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function SportChips() {
  const { sport, skiOffSeason } = useContext(SportChoice);
  const setSport = useContext(SetSportChoice);
  return (
    <>
      <div className="relative z-[8] flex items-center gap-2 py-1.5">
        <div role="group" aria-label="Choose a sport" className="flex min-w-0 items-center gap-2">
          {HOME_PRIMARY_CHIPS.map((chip) => (
            <button
              key={chip.key}
              type="button"
              aria-pressed={chip.key === sport}
              onClick={() => setSport(chip.key)}
              data-ph-capture-attribute-home-cta={`conditions-chip-${chip.key}`}
              className={`${chipClass(chip.key === sport)} shrink-0`}
            >
              {chip.label}
            </button>
          ))}
          <MoreSportsMenu />
        </div>
        {/* md+: the board link sits at the end of the chip row, clear of the map card. */}
        <div className="ml-auto hidden shrink-0 md:block">
          <LeaderboardLink variant="text" />
        </div>
      </div>
      <p className="sr-only" aria-live="polite">
        {homeSportMessage(sport, skiOffSeason)}
      </p>
    </>
  );
}

export function SportPanel({ sport, children }: { sport: HomeSportToggle; children: ReactNode }) {
  const current = useContext(SportChoice).sport;
  return <div hidden={current !== sport}>{children}</div>;
}

/** Menu sports without homepage cards get one card linking to their leaderboard. */
export function LinkOnlySportCard() {
  const { sport } = useContext(SportChoice);
  const entry = moreSport(sport);
  if (!entry || entry.hasCards) return null;
  return (
    <div className="pb-[22px] pt-2.5 md:pt-6">
      <Link
        href={entry.href}
        data-ph-capture-attribute-home-cta="conditions-sport-leaderboard"
        className="group flex items-center gap-3.5 rounded-[18px] border border-line bg-white px-4 py-[18px] no-underline shadow-[0_10px_26px_rgba(22,35,59,0.07)] transition-[transform,box-shadow] hover:-translate-y-[3px] hover:shadow-[0_18px_36px_rgba(22,35,59,0.12)] motion-reduce:transition-none md:max-w-[560px]"
      >
        <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-teal/10 text-teal">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
            <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
          </svg>
        </span>
        <span className="flex min-w-0 grow flex-col gap-[3px]">
          <span className="text-[16px] font-bold leading-[1.3] text-navy">See the {entry.lower} leaderboard</span>
          <span className="text-[13px] leading-[1.45] text-[#566072]">
            Live GO scores out of 100 for the {entry.lower} spots we track.
          </span>
        </span>
        <span aria-hidden="true" className="shrink-0 text-teal-dark transition-transform group-hover:translate-x-1">
          <ArrowRightIcon />
        </span>
      </Link>
    </div>
  );
}

/**
 * The board link for the chosen sport. "button" is the phone treatment under
 * the cards; "text" is the md+ arrow link at the right end of the chip row.
 */
export function LeaderboardLink({ variant = "button" }: { variant?: "button" | "text" }) {
  const { sport, skiOffSeason } = useContext(SportChoice);
  const link = homeBoardLink(sport, skiOffSeason);
  const className =
    variant === "text"
      ? "group inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap px-1 text-[14.5px] font-semibold text-teal-dark no-underline hover:underline hover:underline-offset-[3px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
      : "group flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-navy px-4 text-[14.5px] font-semibold text-white no-underline transition-colors hover:bg-navy-hover";
  return (
    <Link href={link.href} data-ph-capture-attribute-home-cta="conditions-leaderboard" className={className}>
      {link.label}
      <span aria-hidden="true" className="transition-transform group-hover:translate-x-1 motion-reduce:transition-none">
        <ArrowRightIcon />
      </span>
    </Link>
  );
}
